import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'

import { anim, prog } from '../motion'

const range = PropTypes.arrayOf(PropTypes.number)

/**
 * Text, der sich Wort für Wort aus dem Grau ins Volle schreibt (wie auf den
 * Apple-Produktseiten). `at` ist der Bereich von --l für den ganzen Satz.
 */
export function Words({ text, at, dim = 0.16, className }) {
  const words = text.split(' ')
  const step = (at[1] - at[0]) / words.length
  return (
    <span className={className}>
      {words.map((word, i) => (
        <span
          key={i}
          className='cin-word'
          style={{
            ...prog([at[0] + step * i, at[0] + step * (i + 1.6)]),
            opacity: `calc(${dim} + ${1 - dim} * var(--p))`
          }}
        >
          {word}{' '}
        </span>
      ))}
    </span>
  )
}

Words.propTypes = {
  text: PropTypes.string.isRequired,
  at: range.isRequired,
  dim: PropTypes.number,
  className: PropTypes.string
}

/** Eine Zeile, die im Bereich `at` auftaucht und optional in `out` wieder geht. */
export function Line({
  at,
  out,
  y = 48,
  oy = -48,
  s = 1,
  className,
  children
}) {
  return (
    <div
      className={clsx('cin-line', className)}
      style={anim({ at, out, y, oy, s })}
    >
      {children}
    </div>
  )
}

Line.propTypes = {
  at: range.isRequired,
  out: range,
  y: PropTypes.number,
  oy: PropTypes.number,
  s: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.node
}

/**
 * Skaliert einen Block fester Größe (w × h) passend in seinen Container.
 * `maxW`/`maxH` begrenzen den Anteil an der Containerfläche, `narrow` ersetzt
 * sie in schmalen Containern.
 */
export function Fit({
  w,
  h,
  maxW = 1,
  maxH = 1,
  narrow,
  className,
  style,
  children
}) {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    const host = el?.parentElement
    if (!el || !host) return undefined
    const fit = () => {
      // Unter 720px Breite gelten optional andere Grenzen (wie die Container-Query im CSS)
      const box = narrow && host.clientWidth < 720 ? narrow : { maxW, maxH }
      const s = Math.min(
        (host.clientWidth * box.maxW) / w,
        (host.clientHeight * box.maxH) / h
      )
      el.style.setProperty('--fit', String(s))
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(host)
    return () => ro.disconnect()
  }, [w, h, maxW, maxH, narrow])

  return (
    <div
      ref={ref}
      className={clsx('cin-fit', className)}
      style={{ width: w, height: h, ...style }}
    >
      {children}
    </div>
  )
}

Fit.propTypes = {
  w: PropTypes.number.isRequired,
  h: PropTypes.number.isRequired,
  maxW: PropTypes.number,
  maxH: PropTypes.number,
  narrow: PropTypes.shape({ maxW: PropTypes.number, maxH: PropTypes.number }),
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node
}
